import { useQueryClient } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";
import { useAuthStore } from "../store/useAuthStore";
import { syncBlockedUsers } from "../store/useBlockStore";
import { clearChatData } from "./clearChatData";
import { UnreadTitle } from "./components/UnreadTitle";
import { userIdFromToken } from "./lib/jwt";
import { listenForNotificationClicks, notifyIncoming, subscribeWebPush, unsubscribeWebPush } from "./notifications/notify";
import { useConversationPrefs } from "./store/conversationPrefsStore";
import { useChatDrafts } from "./store/draftsStore";
import { useChatOutbox } from "./store/outboxStore";

/**
 * The app's single chat connection owner. After login it lazy-loads the
 * realtime runtime (socket.io-client and friends stay out of the main
 * bundle for guests) and starts it for the signed-in user. On logout or a
 * user switch it tears the runtime down and wipes that user's chat data:
 * Query cache, typing/presence, the dock, drafts and the outbox.
 */
export function ChatProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const profileId = useAuthStore((s) => s.id);
  const token = useAuthStore((s) => s.accessToken);
  const userId = isAuthenticated ? (profileId ?? userIdFromToken(token)) : null;

  useEffect(() => {
    if (userId === null) return;
    useChatOutbox.getState().claim(userId);
    useChatDrafts.getState().claim(userId);
    useConversationPrefs.getState().claim(userId);

    let stop: (() => void) | null = null;
    let cancelled = false;
    void import("./runtime/runtime")
      .then(({ startChatRuntime }) => (cancelled ? null : startChatRuntime({ queryClient, userId, onNewIncoming: (m) => notifyIncoming(queryClient, m) })))
      .then((stopRuntime) => {
        if (!stopRuntime) return;
        if (cancelled) stopRuntime();
        else stop = stopRuntime;
      });

    void subscribeWebPush();
    void syncBlockedUsers();
    const stopClicks = listenForNotificationClicks();

    return () => {
      cancelled = true;
      stop?.();
      stopClicks();
    };
  }, [queryClient, userId]);

  // Signed out (or another account signed in): nothing of the previous user's chat survives.
  useEffect(() => {
    let previous = useAuthStore.getState().isAuthenticated ? userId : null;
    return useAuthStore.subscribe((state) => {
      const next = state.isAuthenticated ? (state.id ?? userIdFromToken(state.accessToken)) : null;
      if (previous !== null && next !== previous) {
        void unsubscribeWebPush();
        clearChatData(queryClient);
      }
      previous = next;
    });
    // Subscribed once; `previous` tracks the user from then on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  return (
    <>
      <UnreadTitle />
      {children}
    </>
  );
}
